/*
 * Copyright 2026 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {AriaLabelingProps, DOMProps, DOMRef, forwardRefType} from '@react-types/shared';
import {AttachmentCard, AttachmentPreviewContext, isAttachmentLoading} from './AttachmentList';
import {filterDOMProps} from 'react-aria/filterDOMProps';
import {focusRing, style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {forwardRef, ReactNode} from 'react';
import {ListBox, ListBoxItem, ListBoxItemProps, ListBoxProps} from 'react-aria-components/ListBox';
import {mergeStyles} from '@react-spectrum/s2/mergeStyles';
import {scrollFade} from './style/style-macro' with {type: 'macro'};
import {StyleString} from '@react-spectrum/s2/style' with {type: 'macro'};
import {useDOMRef} from './useDOMRef';

export interface AttachmentGridProps<T>
  extends
    DOMProps,
    AriaLabelingProps,
    Pick<ListBoxProps<T>, 'items' | 'children' | 'dependencies' | 'onAction'> {
  /**
   * The alignment of attachments within the grid.
   *
   * @default 'start'
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Spectrum-defined styles, returned by the `style()` macro.
   */
  styles?: StyleString;
}

// Grid items are expected to be either all thumbnails or cards and the same size.
const hasContent = ':has([data-slot=content])';

const gridStyles = style({
  display: {
    default: 'flex',
    [hasContent]: 'grid'
  },
  flexWrap: 'wrap',
  alignItems: 'start',
  gridTemplateColumns: {
    [hasContent]: 'repeat(auto-fill, minmax(240px, 1fr))'
  },
  justifyContent: {
    align: {
      start: 'normal',
      center: 'center',
      end: 'end'
    }
  },
  gap: 8,
  padding: 4,
  scrollPaddingY: 4,
  maxHeight: 'inherit',
  overflowY: 'auto',
  overflowX: 'clip',
  boxSizing: 'border-box',
  borderRadius: 'lg',
  ...focusRing()
});

/**
 * An AttachmentGrid displays file attachments as a wrapping, vertically-scrolling grid of
 * thumbnails. Unlike AttachmentList, it does not support selection or removal.
 */
export const AttachmentGrid = (forwardRef as forwardRefType)(function AttachmentGrid<T>(
  props: AttachmentGridProps<T>,
  ref: DOMRef<HTMLDivElement>
) {
  let {styles, items, children, dependencies, align = 'start', ...otherProps} = props;
  let domRef = useDOMRef(ref);

  return (
    <ListBox
      {...otherProps}
      layout="grid"
      selectionMode="none"
      items={items}
      dependencies={dependencies}
      ref={domRef}
      className={renderProps =>
        mergeStyles(gridStyles({...renderProps, align}), styles) + ' ' + scrollFade({y: 36})
      }>
      {children}
    </ListBox>
  );
});

export interface AttachmentGridItemProps
  extends AriaLabelingProps, Pick<ListBoxItemProps, 'id' | 'textValue'> {
  /** The size of the Card. */
  size?: 'XS' | 'S' | 'M' | 'L' | 'XL';
  /** Whether the attachment has an error. */
  isInvalid?: boolean;
  /** The upload progress percentage (0-100). Values under 100 render a loading state. */
  uploadProgress?: number;
  /** The children of the AttachmentGridItem. */
  children: ReactNode;
  /** Handler that is called when a user performs an action on the attachment. */
  onAction?: () => void;
  /**
   * Spectrum-defined styles, returned by the `style()` macro.
   */
  styles?: StyleString;
}

const itemStyles = style({
  flexShrink: 0,
  flexGrow: 0,
  flexBasis: '0%',
  position: 'relative',
  ...focusRing(),
  borderRadius: 'lg'
});

/**
 * AttachmentGridItem displays an individual file attachment thumbnail within an AttachmentGrid.
 */
export const AttachmentGridItem = forwardRef(function AttachmentGridItem(
  props: AttachmentGridItemProps,
  ref: DOMRef<HTMLDivElement>
) {
  let {id, textValue, styles, isInvalid, size = 'M', children, onAction, ...otherProps} = props;
  let domRef = useDOMRef(ref);
  let isLoading = isAttachmentLoading(props.uploadProgress);

  return (
    <ListBoxItem
      id={id}
      {...filterDOMProps(otherProps, {labelable: true})}
      textValue={textValue}
      onAction={onAction}
      ref={domRef}
      className={renderProps => mergeStyles(itemStyles({...renderProps}), styles)}>
      <AttachmentCard size={size} isInvalid={isInvalid} isLoading={isLoading}>
        <AttachmentPreviewContext.Provider
          value={{
            isInvalid: !!isInvalid,
            isDisabled: false,
            uploadProgress: props.uploadProgress ?? 100,
            size
          }}>
          {children}
        </AttachmentPreviewContext.Provider>
      </AttachmentCard>
    </ListBoxItem>
  );
});
